/**
 * Project progress buckets — coarse-grained part distribution across the
 * 18-stage pipeline, used by the "where is this project sitting" visual
 * on cards and headers.
 *
 * Buckets group the 18 pipeline stages into four manager-relevant phases:
 *
 *   Procurement  parts at stage ≤ 8 (or 0 / pre-pipeline)
 *                — getting parts in and inspection offered
 *   Inspection   parts at stages 9–13
 *                — client inspection cycle through delivery + functional check
 *   Billing      parts at stages 14–17
 *                — I-Note / CRV / Invoice / Cheque Released
 *   Closed       parts at stage 18 (Cheque Cleared)
 *
 * The boundary at 8/9 matches the spec's "client-facing inspection begins"
 * transition. Boundary at 13/14 matches the "delivery proven, paperwork
 * begins" transition. Both are real semantic shifts a manager cares about.
 *
 * Used as:
 *   - SQL aggregation source on the API side (apps/api/src/lib/project-progress.ts)
 *   - Client-side aggregation when full part lists are available (project detail)
 *   - Shared schema in VisibilityRow.partsProgress
 */

export interface ProjectProgressBuckets {
  /** Parts with currentStageNumber ≤ 8 (includes pre-pipeline / 0). */
  procurement: number;
  /** Parts with currentStageNumber 9–13. */
  inspection: number;
  /** Parts with currentStageNumber 14–17. */
  billing: number;
  /** Parts with currentStageNumber === 18. */
  closed: number;
}

export const PROJECT_PROGRESS_BUCKET_KEYS = [
  'procurement',
  'inspection',
  'billing',
  'closed',
] as const;
export type ProjectProgressBucketKey = (typeof PROJECT_PROGRESS_BUCKET_KEYS)[number];

/**
 * Pick the bucket for a single stage number. Falls back to 'procurement' for
 * 0 / negative / out-of-range inputs, mirroring the "pre-pipeline" semantics.
 */
export function bucketForStage(stageNumber: number): ProjectProgressBucketKey {
  if (stageNumber === 18) return 'closed';
  if (stageNumber >= 14) return 'billing';
  if (stageNumber >= 9) return 'inspection';
  return 'procurement';
}

/**
 * Aggregate a flat list of parts into the four buckets. The shape is
 * intentionally minimal — anything with a `currentStageNumber` works.
 */
export function computeProjectProgressBuckets(
  parts: ReadonlyArray<{ currentStageNumber: number }>,
): ProjectProgressBuckets {
  const buckets: ProjectProgressBuckets = {
    procurement: 0,
    inspection: 0,
    billing: 0,
    closed: 0,
  };
  for (const p of parts) {
    buckets[bucketForStage(p.currentStageNumber)] += 1;
  }
  return buckets;
}

/** Total parts across all buckets — convenience for the "X parts" subtitle. */
export function totalPartsInBuckets(b: ProjectProgressBuckets): number {
  return b.procurement + b.inspection + b.billing + b.closed;
}

/**
 * Weighted completion fraction in [0, 1], used by surfaces that want a
 * single-number readout in addition to (or instead of) the distribution bar.
 *
 * Weighting: procurement parts contribute 0, inspection 0.5, billing 0.75,
 * closed 1.0. Chosen so a project halfway through its parts visually reads
 * "half done" rather than "almost done because everything's past stage 6".
 *
 * Returns 0 for empty projects (no parts at all).
 */
export function completionFraction(b: ProjectProgressBuckets): number {
  const total = totalPartsInBuckets(b);
  if (total === 0) return 0;
  const weighted = b.procurement * 0 + b.inspection * 0.5 + b.billing * 0.75 + b.closed * 1;
  return weighted / total;
}
